import { repairUnsplashUrl } from '~/utils/unsplashPhotos'

export function normalizeOptionalUrl(value?: string | null): string | null {
  const url = value?.trim()
  if (!url) return null
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * Point a Laravel `/storage/...` asset at this site's own `/media/...`
 * proxy (see `routeRules`). Serving it cross-origin costs a fresh DNS + TCP +
 * TLS handshake before the first byte, which lands straight on LCP when the
 * asset is a storefront hero image.
 *
 * Host/port must not matter: local `APP_URL` is often `:8002` while
 * `NUXT_PUBLIC_API_BASE` may be same-origin `/api`. Nuxt never serves
 * `/storage`, so any such path is rewritten. Safe without Nuxt context
 * (HMR / SSR after await).
 */
export function sameOriginMedia(url: string): string {
  if (!url) return url
  url = repairUnsplashUrl(url)
  if (url.startsWith('data:') || url.startsWith('blob:')) return url
  if (url.startsWith('/media/')) return url
  if (url.startsWith('/storage/')) return `/media/${url.slice('/storage/'.length)}`
  if (url.startsWith('storage/')) return `/media/${url.slice('storage/'.length)}`
  // Relative public-disk paths from section uploads (`store-sections/…`).
  if (/^(store-sections|store-products|store-collections|store-logos|companies|showcase)\//i.test(url)) {
    return `/media/${url}`
  }

  try {
    const parsed = new URL(url)
    if (parsed.pathname.startsWith('/storage/')) {
      return `/media/${parsed.pathname.slice('/storage/'.length)}${parsed.search}${parsed.hash}`
    }
    if (parsed.pathname.startsWith('/api/media/')) {
      return `/media/${parsed.pathname.slice('/api/media/'.length)}${parsed.search}${parsed.hash}`
    }
    if (parsed.pathname.startsWith('/media/')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  }
  catch {
    // Relative / unknown — fall through.
  }

  return url
}

/**
 * Resolve a chat/API media path to a browser-loadable URL.
 *
 * Resources return relative paths like `storage/foo.jpg`. Joining those onto
 * `apiBase` (`…/api`) produces `…/api/storage/…`, which 404s — storage lives
 * on the API origin root. Absolute / already-proxied URLs pass through.
 */
export function resolveApiMedia(path: string | null | undefined): string {
  if (!path) return ''
  if (
    /^(?:https?:)?\/\//i.test(path)
    || path.startsWith('data:')
    || path.startsWith('/media/')
    || path.startsWith('/storage/')
  ) {
    return sameOriginMedia(path)
  }

  const nuxtApp = tryUseNuxtApp()
  if (!nuxtApp) return path

  const base = String(useRuntimeConfig(nuxtApp).public.apiBase || '')
  if (!base) return path

  const relative = path.replace(/^\//, '')
  // Same-origin `/api` (local Nuxt proxy): skip joining onto apiBase.
  if (base.startsWith('/')) {
    return sameOriginMedia(relative.startsWith('storage/') ? `/${relative}` : `/storage/${relative}`)
  }

  let origin: string
  try {
    origin = new URL(base).origin
  }
  catch {
    return path
  }

  const absolute = relative.startsWith('storage/')
    ? `${origin}/${relative}`
    : `${origin}/storage/${relative}`

  return sameOriginMedia(absolute)
}
